import { useState } from 'react';
import { benefitsApi } from '../../../api/benefits.js';

const RELATIONSHIPS = ['spouse', 'child', 'domestic_partner', 'other'];

export default function Dependents() {
  const [employeeId, setEmployeeId] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  async function load(eid) {
    if (!eid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await benefitsApi.listDependents({ employee_id: eid });
      setItems(data?.items || []);
      setLoaded(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    // Capture the form node before any await: React nulls `e.currentTarget`
    // once the event handler returns, so reading it post-await throws.
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await benefitsApi.createDependent({
        employee_id: employeeId,
        name: fd.get('name'),
        relationship: fd.get('relationship'),
        dob: fd.get('dob') || null,
        is_beneficiary: fd.get('is_beneficiary') === 'on',
        beneficiary_pct: fd.get('beneficiary_pct') ? Number(fd.get('beneficiary_pct')) : null,
      });
      form.reset();
      setShowForm(false);
      load(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add dependent');
    }
  }

  async function del(id) {
    try {
      await benefitsApi.deleteDependent(id);
      load(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  }

  const totalBenePct = items
    .filter((d) => d.is_beneficiary)
    .reduce((s, d) => s + Number(d.beneficiary_pct || 0), 0);

  return (
    <section data-testid="benefits-dependents-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <h1>Dependents & Beneficiaries</h1>
      </header>

      {error && <div className="alert error" data-testid="benefits-dependents-error">{error}</div>}

      <div className="card form-card">
        <label>Employee ID
          <input
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            data-testid="benefits-dep-empid-input"
            placeholder="uuid"
          />
        </label>
        <button className="btn primary" onClick={() => load(employeeId)} disabled={!employeeId || loading} data-testid="benefits-dep-load">
          Load Dependents
        </button>
      </div>

      {loaded && (
        <>
          <div className="page-header">
            <div>
              <strong>{items.length} dependents</strong>
              {totalBenePct > 0 && <span className="muted"> · Beneficiary total: {totalBenePct}%</span>}
            </div>
            <button className="btn primary" data-testid="benefits-dep-add" onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Close' : '+ Add Dependent'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={submit} className="card form-card grid-form">
              <label>Name<input name="name" data-testid="benefits-dep-name-input" required /></label>
              <label>Relationship
                <select name="relationship" data-testid="benefits-dep-rel-input" defaultValue="child">
                  {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label>Date of Birth<input name="dob" type="date" data-testid="benefits-dep-dob-input" /></label>
              <label>Beneficiary?
                <input name="is_beneficiary" type="checkbox" data-testid="benefits-dep-bene-input" />
              </label>
              <label>Beneficiary %<input name="beneficiary_pct" type="number" min="0" max="100" step="0.01" data-testid="benefits-dep-pct-input" /></label>
              <div className="full actions">
                <button className="btn primary" type="submit" data-testid="benefits-dep-submit">Add</button>
              </div>
            </form>
          )}

          <div className="card">
            <table className="data-table" data-testid="benefits-dependent-list">
              <thead>
                <tr><th>Name</th><th>Relationship</th><th>DOB</th><th>Beneficiary</th><th>%</th><th></th></tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} data-testid={`benefits-dep-row-${d.id}`}>
                    <td>{d.name}</td>
                    <td>{d.relationship}</td>
                    <td>{d.dob ? String(d.dob).slice(0, 10) : '—'}</td>
                    <td>{d.is_beneficiary ? 'Yes' : '—'}</td>
                    <td>{d.is_beneficiary ? `${d.beneficiary_pct || 0}%` : '—'}</td>
                    <td>
                      <button className="btn ghost small danger" data-testid={`benefits-dep-delete-${d.id}`} onClick={() => del(d.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="muted">No dependents added yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
